import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  onSnapshot,
  collection,
  addDoc,
  query,
  orderBy,
  where,
  limit,
  getDocs,
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from './AuthContext';
import { usePet } from './PetContext';
import {
  BeanProfile,
  CompanionEntry,
  XPSource,
  XPEvent,
  RoomieFeedEntry,
  XP_AMOUNTS,
  BEAN_LEVEL_THRESHOLDS,
  PET_COLORS,
  STORY_TEXT_MAX_LEN,
} from '../types';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function todayISO(): string {
  return new Date().toISOString().split('T')[0];
}

/** ISO date (YYYY-MM-DD) of Monday of the calendar week containing `d`. */
function mondayISO(d: Date = new Date()): string {
  const day = d.getDay(); // 0 = Sun, 1 = Mon … 6 = Sat
  const offset = day === 0 ? -6 : 1 - day;
  const m = new Date(d);
  m.setDate(d.getDate() + offset);
  return m.toISOString().split('T')[0];
}

function levelFromXP(xp: number): number {
  let level = 1;
  for (let i = BEAN_LEVEL_THRESHOLDS.length - 1; i >= 0; i--) {
    if (xp >= BEAN_LEVEL_THRESHOLDS[i]) {
      level = i + 1;
      break;
    }
  }
  return Math.min(level, BEAN_LEVEL_THRESHOLDS.length);
}

function xpForNextLevel(level: number): number {
  return BEAN_LEVEL_THRESHOLDS[Math.min(level, BEAN_LEVEL_THRESHOLDS.length - 1)] ?? Infinity;
}

const ONE_TIME_XP_SOURCES: XPSource[] = ['medical_card', 'onboarding'];

const DEFAULT_PROFILE: Omit<BeanProfile, 'roomie_group_id' | 'roomie_nickname' | 'roomie_color'> & {
  roomie_group_id: null;
  roomie_nickname: null;
  roomie_color: null;
} = {
  xp_total: 0,
  level: 1,
  current_streak: 0,
  longest_streak: 0,
  last_log_date: null,
  last_awarded: {},
  roomie_consent: false,
  roomie_group_id: null,
  roomie_nickname: null,
  roomie_color: null,
  public_view: true,
  week_start: null,
  week_xp_start: 0,
  story_chip: null,
  story_chip_expires: null,
  story_text: null,
};

function companionScore(level: number, current_streak: number): number {
  return level * 100 + current_streak;
}

// ─── Celebration ─────────────────────────────────────────────────────────────

export type CelebrationEvent =
  | { type: 'level_up'; level: number }
  | { type: 'streak_milestone'; streak: number };

// ─── Context shape ────────────────────────────────────────────────────────────

interface BeanContextType {
  profile: BeanProfile | null;
  xpEvents: XPEvent[];
  roomieFeed: RoomieFeedEntry[];
  /** Universal Koda Companions leaderboard (top by score, public only) */
  companions: CompanionEntry[];
  /** Weekly Koda Companions leaderboard (top by XP gained this week, public only) */
  companionsWeek: CompanionEntry[];
  pendingCelebration: CelebrationEvent | null;
  clearCelebration: () => void;
  /** Toggle whether this user appears on the public Koda Companions leaderboard */
  setPublicView: (visible: boolean) => Promise<void>;
  /** Set or clear the public companion story chip + free-text. Pass null/empty string to clear. */
  setStory: (chipKey: string | null, text: string | null) => Promise<void>;
  /** Toggle a reaction (emoji) on another companion's leaderboard entry */
  reactToCompanion: (companionDocId: string, emoji: string) => Promise<void>;
  /** Award XP for a given source — deduped once per calendar day per source (or once-ever for one-time sources) */
  awardXP: (source: XPSource) => Promise<void>;
  /** Call after every daily log to update streak */
  checkStreak: () => Promise<void>;
  /** Undo today's streak increment if today's log is no longer complete */
  rollbackStreak: () => Promise<void>;
  /** Update roomie profile fields */
  updateRoomieProfile: (updates: Partial<Pick<BeanProfile, 'roomie_consent' | 'roomie_group_id' | 'roomie_nickname' | 'roomie_color'>>) => Promise<void>;
  /** React to a roomie feed entry */
  reactToFeedEntry: (entryId: string, emoji: string) => Promise<void>;
  levelFromXP: (xp: number) => number;
  xpForNextLevel: (level: number) => number;
}

const BeanContext = createContext<BeanContextType | undefined>(undefined);

function dedupeCompanionsByPetId(rows: CompanionEntry[]) {
  const byPetId = new Map<string, CompanionEntry>();

  for (const row of rows) {
    const petId = row.pet_id;
    if (!petId) continue;

    const existing = byPetId.get(petId);
    if (!existing) {
      byPetId.set(petId, row);
      continue;
    }

    const existingUpdatedAt = new Date((existing as CompanionEntry & { updated_at?: string }).updated_at ?? 0).getTime();
    const rowUpdatedAt = new Date((row as CompanionEntry & { updated_at?: string }).updated_at ?? 0).getTime();
    if (rowUpdatedAt >= existingUpdatedAt) {
      byPetId.set(petId, row);
    }
  }

  return Array.from(byPetId.values());
}

// ─── Provider ─────────────────────────────────────────────────────────────────

export function BeanProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const { activePet, activePetId, activePetOwnerUid, isActivePetSubordinate } = usePet();
  const [profile, setProfile] = useState<BeanProfile | null>(null);
  const [xpEvents, setXpEvents] = useState<XPEvent[]>([]);
  const [roomieFeed, setRoomieFeed] = useState<RoomieFeedEntry[]>([]);
  const [companions, setCompanions] = useState<CompanionEntry[]>([]);
  const [companionsWeek, setCompanionsWeek] = useState<CompanionEntry[]>([]);
  const [pendingCelebration, setPendingCelebration] = useState<CelebrationEvent | null>(null);

  const petOwnerUid = activePetOwnerUid ?? user?.id ?? null;
  const hasActivePetContext = Boolean(user && petOwnerUid && activePetId);
  // Doc ID is always keyed by the pet owner — never the co-carer.
  // This ensures setPublicView / setStory always target the correct document
  // and prevents duplicate entries when a co-carer opens a shared pet.
  const companionDocId = hasActivePetContext ? `${petOwnerUid}_${activePetId}` : null;

  // ── Bootstrap & real-time listener on bean profile ──────────────────────────
  useEffect(() => {
    if (!user || !hasActivePetContext || !petOwnerUid || !activePetId) {
      setProfile(null);
      setXpEvents([]);
      setRoomieFeed([]);
      return;
    }

    const profileRef = doc(db, 'users', petOwnerUid, 'pets', activePetId, 'fellowship', 'profile');
    const legacyProfileRef = doc(db, 'users', user.id, 'fellowship', 'profile');

    // Ensure doc exists on first load
    getDoc(profileRef).then(async (snap) => {
      if (snap.exists()) return;
      // Migrate owner's legacy single-profile BEAN data once, otherwise start fresh.
      if (petOwnerUid === user.id) {
        const legacy = await getDoc(legacyProfileRef);
        if (legacy.exists()) {
          await setDoc(profileRef, legacy.data(), { merge: true });
          return;
        }
      }
      await setDoc(profileRef, DEFAULT_PROFILE);
    });

    const unsubProfile = onSnapshot(
      profileRef,
      snap => {
        if (snap.exists()) {
          const data = snap.data() as BeanProfile;
          // Default public_view to true for legacy profiles missing the field
          setProfile({ ...data, public_view: data.public_view !== false });
        }
      },
      (error) => {
        console.error('[BeanContext] Failed to read users/{ownerUid}/pets/{petId}/fellowship/profile:', error);
      }
    );

    // Recent XP events (last 20)
    const xpQuery = query(
      collection(db, 'users', petOwnerUid, 'pets', activePetId, 'fellowship_xp_events'),
      orderBy('timestamp', 'desc'),
      limit(20)
    );
    const unsubXP = onSnapshot(
      xpQuery,
      snap => {
        setXpEvents(snap.docs.map(d => ({ id: d.id, ...d.data() } as XPEvent)));
      },
      (error) => {
        console.error('[BeanContext] Failed to read users/{ownerUid}/pets/{petId}/fellowship_xp_events:', error);
      }
    );

    return () => {
      unsubProfile();
      unsubXP();
    };
  }, [user?.id, hasActivePetContext, petOwnerUid, activePetId]);

  // ── Roomie feed listener (only when in a group) ──────────────────────────────
  useEffect(() => {
    if (!user || !profile?.roomie_group_id || !profile.roomie_consent) {
      setRoomieFeed([]);
      return;
    }

    const feedQuery = query(
      collection(db, 'roomie_groups', profile.roomie_group_id, 'feed'),
      orderBy('timestamp', 'desc'),
      limit(30)
    );
    const unsubFeed = onSnapshot(
      feedQuery,
      snap => {
        setRoomieFeed(snap.docs.map(d => ({ id: d.id, ...d.data() } as RoomieFeedEntry)));
      },
      (error) => {
        if ((error as { code?: string }).code === 'permission-denied') {
          // If roomie groups are locked down by rules or not enabled yet,
          // fail gracefully without noisy console errors in normal app flow.
          setRoomieFeed([]);
          return;
        }
        console.error('[BeanContext] Failed to read roomie_groups/{groupId}/feed:', error);
      }
    );

    return () => unsubFeed();
  }, [user?.id, profile?.roomie_group_id, profile?.roomie_consent]);

  // ── Sync this user's public companion entry whenever profile/active pet changes ──
  useEffect(() => {
    if (!user || !profile || !hasActivePetContext || !petOwnerUid || !activePetId || !companionDocId) return;
    // Co-carers do not own the pet's gamification entry — only the owner ever writes it.
    if (isActivePetSubordinate) return;
    // Don't write until the pet document has loaded; without a real pet name the
    // fallback would incorrectly use the human owner's name.
    if (!activePet?.name?.trim()) return;


      const publicView = profile.public_view !== false;
      const petName = activePet?.name?.trim() || profile.roomie_nickname?.trim() || (user.full_name?.split(' ')[0] ?? 'Koda');
      const petColor = activePet?.color || profile.roomie_color || PET_COLORS[0];
      const level = levelFromXP(profile.xp_total);
      const score = companionScore(level, profile.current_streak);

      // ── Weekly rollover ────────────────────────────────────────────────────
      const currentWeek = mondayISO();
      const profileRef = doc(db, 'users', petOwnerUid, 'pets', activePetId, 'fellowship', 'profile');
    if (profile.week_start !== currentWeek) {
      // Snapshot xp_total as the starting baseline for this week
      setDoc(profileRef, { week_start: currentWeek, week_xp_start: profile.xp_total ?? 0 }, { merge: true })
        .catch(err => console.warn('[BeanContext] Weekly rollover write failed:', err));
      // Skip this sync — listener will refire with rolled-over profile values
      return;
    }
    const weekXpStart = profile.week_xp_start ?? profile.xp_total;
    const weekScore = Math.max(0, profile.xp_total - weekXpStart);

    // ── Story chip + text ───────────────────────────────────────────────────
    const storyChip: string | null = profile.story_chip ?? null;
    const storyText: string | null = profile.story_text ?? null;

    const ref = doc(db, 'public_companions', companionDocId);
    setDoc(ref, {
      id: companionDocId,
      user_id: user.id,
      pet_id: activePetId,
      pet_owner_uid: petOwnerUid,
      pet_name: petName.slice(0, 24),
      pet_color: petColor,
      pet_species: activePet?.species ?? 'otheranimal',
      level,
      xp_total: profile.xp_total ?? 0,
      current_streak: profile.current_streak ?? 0,
      longest_streak: profile.longest_streak ?? 0,
      score,
      week_score: weekScore,
      week_start: currentWeek,
      story_chip: storyChip,
      story_text: storyText,
      public_view: publicView,
      updated_at: new Date().toISOString(),
    }, { merge: true }).catch(err => {
      console.warn('[BeanContext] Failed to sync public_companions doc:', err);
    });
  }, [user?.id, user?.full_name, profile?.xp_total, profile?.current_streak, profile?.longest_streak, profile?.public_view, profile?.roomie_nickname, profile?.roomie_color, profile?.week_start, profile?.week_xp_start, profile?.story_chip, profile?.story_text, activePet?.name, activePet?.color, activePet?.species, hasActivePetContext, petOwnerUid, activePetId, companionDocId, isActivePetSubordinate]);

  // ── Leaderboard listener — top public companions by score ───────────────────
  useEffect(() => {
    if (!user) {
      setCompanions([]);
      return;
    }
    const lbQuery = query(
      collection(db, 'public_companions'),
      orderBy('score', 'desc'),
      limit(50)
    );
    const unsub = onSnapshot(
      lbQuery,
      snap => {
        const rows = dedupeCompanionsByPetId(
          snap.docs.map(d => ({ id: d.id, ...(d.data() as Omit<CompanionEntry, 'id'>) }))
        );
        // Hide opted-out users; always include current user so they can see their own rank
        setCompanions(rows.filter(r => r.public_view !== false || r.user_id === user.id));
      },
      err => {
        if ((err as { code?: string }).code !== 'permission-denied') {
          console.error('[BeanContext] Failed to read public_companions:', err);
        }
        setCompanions([]);
      }
    );
    return () => unsub();
  }, [user?.id]);

  // ── Weekly leaderboard listener — top XP gained this week ──────────────────
  useEffect(() => {
    if (!user) {
      setCompanionsWeek([]);
      return;
    }
    const currentWeek = mondayISO();
    const wkQuery = query(
      collection(db, 'public_companions'),
      where('week_start', '==', currentWeek),
      orderBy('week_score', 'desc'),
      limit(50)
    );
    const unsub = onSnapshot(
      wkQuery,
      snap => {
        const rows = dedupeCompanionsByPetId(
          snap.docs.map(d => ({ id: d.id, ...(d.data() as Omit<CompanionEntry, 'id'>) }))
        );
        setCompanionsWeek(rows.filter(r => (r.public_view !== false || r.user_id === user.id) && (r.week_score ?? 0) > 0));
      },
      err => {
        if ((err as { code?: string }).code !== 'permission-denied') {
          console.error('[BeanContext] Failed to read weekly public_companions:', err);
        }
        setCompanionsWeek([]);
      }
    );
    return () => unsub();
  }, [user?.id]);

  // ── awardXP ──────────────────────────────────────────────────────────────────
  const awardXP = useCallback(async (source: XPSource) => {
    if (!user || !profile || !hasActivePetContext || !petOwnerUid || !activePetId) return;

    const today = todayISO();
    const lastAwarded = profile.last_awarded?.[source];

    // Deduplicate: once-ever for one-time sources, otherwise once per day
    if (ONE_TIME_XP_SOURCES.includes(source)) {
      if (lastAwarded) return;
    } else if (lastAwarded === today) {
      return;
    }

    const amount = XP_AMOUNTS[source];
    const newXP = profile.xp_total + amount;
    const oldLevel = levelFromXP(profile.xp_total);
    const newLevel = levelFromXP(newXP);

    const profileRef = doc(db, 'users', petOwnerUid, 'pets', activePetId, 'fellowship', 'profile');

    await updateDoc(profileRef, {
      xp_total: newXP,
      level: newLevel,
      [`last_awarded.${source}`]: today,
    });

    // Log the event — use setDoc with a pre-generated ID so offline retries are idempotent.
    const xpEventRef = doc(collection(db, 'users', petOwnerUid, 'pets', activePetId, 'fellowship_xp_events'));
    await setDoc(xpEventRef, {
      source,
      amount,
      timestamp: new Date().toISOString(),
    });

    // Trigger level-up celebration
    if (newLevel > oldLevel) {
      setPendingCelebration({ type: 'level_up', level: newLevel });
    }
  }, [user, profile, hasActivePetContext, petOwnerUid, activePetId]);

  // ── checkStreak ───────────────────────────────────────────────────────────────
  const checkStreak = useCallback(async () => {
    if (!user || !profile || !hasActivePetContext || !petOwnerUid || !activePetId) return;

    const today = todayISO();
    if (profile.last_log_date === today) return; // already counted today

    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayISO = yesterday.toISOString().split('T')[0];

    const continued = profile.last_log_date === yesterdayISO;
    const newStreak = continued ? profile.current_streak + 1 : 1;
    const newLongest = Math.max(newStreak, profile.longest_streak);

    const profileRef = doc(db, 'users', petOwnerUid, 'pets', activePetId, 'fellowship', 'profile');
    await updateDoc(profileRef, {
      current_streak: newStreak,
      longest_streak: newLongest,
      last_log_date: today,
      prev_streak: profile.current_streak,
      prev_log_date: profile.last_log_date ?? null,
    });

    // Always push daily_log to roomie feed (with streak count)
    if (
      profile.roomie_consent &&
      profile.roomie_group_id &&
      profile.roomie_nickname &&
      profile.roomie_color
    ) {
      await addDoc(collection(db, 'roomie_groups', profile.roomie_group_id, 'feed'), {
        user_id: user.id,
        nickname: profile.roomie_nickname,
        bean_color: profile.roomie_color,
        action: 'daily_log',
        streak: newStreak,
        timestamp: new Date().toISOString(),
        reactions: {},
      });
    }

    // Streak milestone bonuses: 7, 14, 30 days
    const MILESTONES = [7, 14, 30];
    if (MILESTONES.includes(newStreak)) {
      setPendingCelebration({ type: 'streak_milestone', streak: newStreak });
      await awardXP('streak_milestone');

      // Push streak milestone to roomie feed
      if (
        profile.roomie_consent &&
        profile.roomie_group_id &&
        profile.roomie_nickname &&
        profile.roomie_color
      ) {
        await addDoc(collection(db, 'roomie_groups', profile.roomie_group_id, 'feed'), {
          user_id: user.id,
          nickname: profile.roomie_nickname,
          bean_color: profile.roomie_color,
          action: 'streak_milestone',
          streak: newStreak,
          timestamp: new Date().toISOString(),
          reactions: {},
        });
      }
    }
  }, [user, profile, awardXP, hasActivePetContext, petOwnerUid, activePetId]);

  // ── rollbackStreak ──────────────────────────────────────────────────────────────
  const rollbackStreak = useCallback(async () => {
    if (!user || !profile || !hasActivePetContext || !petOwnerUid || !activePetId) return;
    if (profile.last_log_date !== todayISO()) return; // only relevant if today was counted
    const profileRef = doc(db, 'users', petOwnerUid, 'pets', activePetId, 'fellowship', 'profile');
    await updateDoc(profileRef, {
      current_streak: profile.prev_streak ?? 0,
      last_log_date: profile.prev_log_date ?? null,
      prev_streak: null,
      prev_log_date: null,
    });
  }, [user, profile, hasActivePetContext, petOwnerUid, activePetId]);

  // ── updateRoomieProfile ───────────────────────────────────────────────────────
  const updateRoomieProfile = useCallback(async (
    updates: Partial<Pick<BeanProfile, 'roomie_consent' | 'roomie_group_id' | 'roomie_nickname' | 'roomie_color'>>
  ) => {
    if (!user || !hasActivePetContext || !petOwnerUid || !activePetId) return;
    const profileRef = doc(db, 'users', petOwnerUid, 'pets', activePetId, 'fellowship', 'profile');
    await setDoc(profileRef, updates, { merge: true });

    // If nickname or color changed, push a 'changed_look' event to the feed
    const currentProfile = profile;
    if (
      currentProfile?.roomie_consent &&
      currentProfile.roomie_group_id &&
      (updates.roomie_nickname || updates.roomie_color)
    ) {
      const nickname = updates.roomie_nickname ?? currentProfile.roomie_nickname ?? '';
      const bean_color = updates.roomie_color ?? currentProfile.roomie_color ?? '';
      await addDoc(collection(db, 'roomie_groups', currentProfile.roomie_group_id, 'feed'), {
        user_id: user.id,
        nickname,
        bean_color,
        action: 'changed_look',
        timestamp: new Date().toISOString(),
        reactions: {},
      });
    }
  }, [user, profile, hasActivePetContext, petOwnerUid, activePetId]);

  // ── reactToFeedEntry ─────────────────────────────────────────────────────────
  const reactToFeedEntry = useCallback(async (entryId: string, emoji: string) => {
    if (!user || !profile?.roomie_group_id) return;

    const entryRef = doc(db, 'roomie_groups', profile.roomie_group_id, 'feed', entryId);
    const snap = await getDoc(entryRef);
    if (!snap.exists()) return;

    const data = snap.data() as RoomieFeedEntry;
    const existing: string[] = data.reactions?.[emoji] ?? [];
    const alreadyReacted = existing.includes(user.id);

    // Toggle reaction
    const updated = alreadyReacted
      ? existing.filter(id => id !== user.id)
      : [...existing, user.id];

    // Keep nicknames in sync so recipients can see who reacted
    const myNickname = profile.roomie_nickname ?? '';
    const existingNames: string[] = data.reaction_nicknames?.[emoji] ?? [];
    const updatedNames = alreadyReacted
      ? existingNames.filter(n => n !== myNickname)
      : myNickname ? [...existingNames.filter(n => n !== myNickname), myNickname] : existingNames;

    await updateDoc(entryRef, {
      [`reactions.${emoji}`]: updated,
      [`reaction_nicknames.${emoji}`]: updatedNames,
    });
  }, [user, profile?.roomie_group_id, profile?.roomie_nickname]);

  // ── setPublicView ────────────────────────────────────────────────────────────
  const setPublicView = useCallback(async (visible: boolean) => {
    if (!user || !hasActivePetContext || !petOwnerUid || !activePetId || !companionDocId) return;
    const profileRef = doc(db, 'users', petOwnerUid, 'pets', activePetId, 'fellowship', 'profile');
    await setDoc(profileRef, { public_view: visible }, { merge: true });
    // Also reflect immediately on the public companion doc so the leaderboard updates
    await setDoc(doc(db, 'public_companions', companionDocId), { public_view: visible, updated_at: new Date().toISOString() }, { merge: true })
      .catch(err => console.warn('[BeanContext] Failed to update public_companions visibility:', err));
  }, [user, hasActivePetContext, petOwnerUid, activePetId, companionDocId]);

  // ── setStory (chip + text) ───────────────────────────────────────────────────────
  const setStory = useCallback(async (chipKey: string | null, text: string | null) => {
    if (!user || !hasActivePetContext || !petOwnerUid || !activePetId || !companionDocId) return;
    const cleanText = text ? text.slice(0, STORY_TEXT_MAX_LEN).trim() : null;
    const profileRef = doc(db, 'users', petOwnerUid, 'pets', activePetId, 'fellowship', 'profile');
    await setDoc(profileRef, {
      story_chip: chipKey,
      story_text: cleanText && cleanText.length > 0 ? cleanText : null,
      story_chip_expires: null,
    }, { merge: true });
    // Mirror onto the public companion doc immediately
    await setDoc(doc(db, 'public_companions', companionDocId), {
      story_chip: chipKey,
      story_text: cleanText && cleanText.length > 0 ? cleanText : null,
      updated_at: new Date().toISOString(),
    }, { merge: true })
      .catch(err => console.warn('[BeanContext] Failed to update public_companions story:', err));
  }, [user, hasActivePetContext, petOwnerUid, activePetId, companionDocId]);

  // ── reactToCompanion ─────────────────────────────────────────────────────────
  const reactToCompanion = useCallback(async (companionDocId: string, emoji: string) => {
    if (!user) return;
    const ref = doc(db, 'public_companions', companionDocId);
    const snap = await getDoc(ref);
    if (!snap.exists()) return;
    const data = snap.data() as CompanionEntry;
    const existing: string[] = data.reactions?.[emoji] ?? [];
    const updated = existing.includes(user.id)
      ? existing.filter(id => id !== user.id)
      : [...existing, user.id];
    await updateDoc(ref, { [`reactions.${emoji}`]: updated });
  }, [user]);

  const clearCelebration = useCallback(() => setPendingCelebration(null), []);

  const contextValue = useMemo(() => ({
    profile,
    xpEvents,
    roomieFeed,
    companions,
    companionsWeek,
    pendingCelebration,
    clearCelebration,
    setPublicView,
    setStory,
    reactToCompanion,
    awardXP,
    checkStreak,
    rollbackStreak,
    updateRoomieProfile,
    reactToFeedEntry,
    levelFromXP,
    xpForNextLevel,
  }), [profile, xpEvents, roomieFeed, companions, companionsWeek, pendingCelebration, clearCelebration, setPublicView, setStory, reactToCompanion, awardXP, checkStreak, rollbackStreak, updateRoomieProfile, reactToFeedEntry]);

  return (
    <BeanContext.Provider value={contextValue}>
      {children}
    </BeanContext.Provider>
  );
}

export function useBean() {
  const ctx = useContext(BeanContext);
  if (!ctx) throw new Error('useBean must be used within a BeanProvider');
  return ctx;
}
